import { TagProps } from "antd";
import { ApiService } from "api/api.service";
import { T_AffiliatePlan_PaymentType, T_AffiliatePlanType } from "dtos/affiliate.plan.dto";
import { BlogStatusTypes, BlogVisibilityTypes } from "dtos/blog.dto";
import { T_Category_Type } from "dtos/category.dto";
import { LeadStatus } from "dtos/lead.dto";
import { Assignment, CASE_STATUS } from "dtos/leadCases.dto";
import { TRANSACTION_STATUS } from "dtos/ledger.dto";
import { ReactElement } from "react";
import {
  TbAddressBook,
  TbArrowsSplit,
  TbBrandLinkedin,
  TbBriefcase,
  TbLinkOff,
  TbMail,
  TbPhone,
  TbPlayerPause,
  TbProgressHelp,
  TbTrash,
  TbUserPlus,
  TbX,
} from "react-icons/tb";

export const PROJECT_INFO = {
  logo: "/logo.png",
  logoWhite: "/logo-white.png",
  favicon: "/favicon.png",
  name: "FCDS Admin",
  layout: {
    headerHeight: 50,
  },
  colors: {
    // primary: "#03FC96",
    primary: "#6f4fef",
    // primary: "#107754",
    // link: "#054746",
    link: "#6f4fef",
    darkBg: "#2e1065",
  },
};

export const grayColours = {
  50: "#f9fafb",
  100: "#f3f4f6",
  200: "#e5e7eb",
  300: "#d1d5db",
  400: "#9ca3af",
  500: "#6b7280",
  600: "#4b5563",
  700: "#374151",
  800: "#1f2937",
  900: "#111827",
  950: "#030712",
};

export const LEAD_STATUS_COLOURS: Record<LeadStatus, TagProps["color"]> = {
  [LeadStatus.CREATED]: "blue",
  [LeadStatus.OPEN]: "geekblue",
  [LeadStatus.ASSIGNED]: "purple",
  [LeadStatus.MEETING_PENDING]: "orange",
  [LeadStatus.MEETING_DONE]: "lime",
  [LeadStatus.RESPONSE_AWAITED]: "magenta",
  [LeadStatus.CONVERTIBLE]: "cyan",
  [LeadStatus.ONBOARDED]: "green",
  [LeadStatus.CLOSED]: "default",
};

export const BLOG_STATUS_COLOURS: Record<BlogStatusTypes, TagProps["color"]> = {
  [BlogStatusTypes.DRAFT]: "blue-inverse",
  [BlogStatusTypes.PUBLISHED]: "green-inverse",
  [BlogStatusTypes.ARCHIVED]: "#9ca3af",
};

export const BLOG_VISIBILITY_COLOURS: Record<BlogVisibilityTypes, TagProps["color"]> = {
  [BlogVisibilityTypes.OPEN_TO_ALL]: "green",
  [BlogVisibilityTypes.MEMBERSHIP_ONLY]: "purple",
  [BlogVisibilityTypes.UNPUBLISHED]: "orange",
  [BlogVisibilityTypes.HIDDEN]: "red",
};
export const CATEGORY_TYPE_COLOURS: Record<T_Category_Type, TagProps["color"]> = {
  [T_Category_Type.BLOG]: "default",
  [T_Category_Type.CUSTOMER_STORY]: "green",
  [T_Category_Type.TESTIMONIAL]: "blue",
};

/* ################# Affiliate Plans ################### */
export const AFFILIATE_PLAN_TYPE_COLOURS: Record<T_AffiliatePlanType, TagProps["color"]> = {
  [T_AffiliatePlanType.ONETIME]: "green",
  [T_AffiliatePlanType.RECURRING]: "blue",
};

export const AFFILIATE_PLAN_PAYMENT_TYPE_COLOURS: Record<T_AffiliatePlan_PaymentType, TagProps["color"]> = {
  [T_AffiliatePlan_PaymentType.FIXED]: "cyan",
  [T_AffiliatePlan_PaymentType.VARIABLE]: "orange",
};

/* #################### CASES #################### */
type T_BatchStatus = NonNullable<ReturnType<typeof ApiService.lead.useGetBatchStatus>["data"]>["stats"];
export const CASE_STATUS_COLOURS: Record<keyof T_BatchStatus, TagProps["color"]> = {
  [CASE_STATUS.CREATED]: "blue",
  [CASE_STATUS.SPLITTED]: "orange",
  [CASE_STATUS.LI_FOUND]: "geekblue",
  [CASE_STATUS.EMAIL_FOUND]: "cyan",
  [CASE_STATUS.PHONE_FOUND]: "purple",
  [CASE_STATUS.LI_NOT_FOUND]: "red",
  [CASE_STATUS.BOTH_FOUND]: "green-inverse",
  [CASE_STATUS.NONE_FOUND]: "red-inverse",
  [CASE_STATUS.DISCARDED]: "default",
  [CASE_STATUS.MISSING_FIELDS]: "orange-inverse",
  hold: "yellow",
  total: "purple-inverse",
};

export const CASE_STATUS_ICONS: { [K in keyof T_BatchStatus]: ReactElement } = {
  [CASE_STATUS.CREATED]: <TbUserPlus />,
  [CASE_STATUS.SPLITTED]: <TbArrowsSplit />,
  [CASE_STATUS.LI_FOUND]: <TbBrandLinkedin />,
  [CASE_STATUS.EMAIL_FOUND]: <TbMail />,
  [CASE_STATUS.PHONE_FOUND]: <TbPhone />,
  [CASE_STATUS.LI_NOT_FOUND]: <TbLinkOff />,
  [CASE_STATUS.BOTH_FOUND]: <TbAddressBook />,
  [CASE_STATUS.NONE_FOUND]: <TbX />,
  [CASE_STATUS.MISSING_FIELDS]: <TbProgressHelp />,
  [CASE_STATUS.DISCARDED]: <TbTrash />,
  hold: <TbPlayerPause />,
  total: <TbBriefcase />,
};

export const ASSIGNMENT_METRICS_CASE_STATUS_MAP: { [K in keyof Assignment]?: CASE_STATUS } = {
  email_found: CASE_STATUS.EMAIL_FOUND,
  phone_found: CASE_STATUS.PHONE_FOUND,
  both_found: CASE_STATUS.BOTH_FOUND,
};

export const CustomStrategyConstants = {
  lawsuit_initial: 995,
  lawsuit_monthly: 145,
  insurance_one_time: 800,
  insurance_monthly: 145,
  service_weekly: 25,
  card_mdr: 0,
  multiplier: 1.22,
  total_interest: 0.22,
  initial_pay: 0,
  bias_amount: 300,
  pushback_factor: 0,
  initial_payment_charge: 0,
  program_part: 10,
  success_part: 12,
};

export const twColors = {
  blue500: "#3b82f6",
  blue600: "#2563eb",
  green500: "#22c55e",
  green600: "#16a34a",
  red500: "#ef4444",
  red600: "#dc2626",
  purple500: "#a855f7",
  purple600: "#9333ea",
  pink500: "#ec4899",
  pink600: "#db2777",
  orange500: "#f97316",
  orange600: "#ea580c",
  gray500: "#6b7280",
  cyan500: "#06b6d4",
  yellow500: "#eab308",
};

export function getLedgerStatusColors(status: TRANSACTION_STATUS): string {
  switch (status) {
    case TRANSACTION_STATUS.PROPOSED:
      return "#A0AEC0";
    case TRANSACTION_STATUS.PENDING:
      return "#F6E05E";
    case TRANSACTION_STATUS.PROCESSING:
      return "#63B3ED";
    case TRANSACTION_STATUS.PAID:
      return "#48BB78";
    case TRANSACTION_STATUS.FAILED:
      return "#F56565";
    case TRANSACTION_STATUS.CANCELLED:
      return "#718096";
    case TRANSACTION_STATUS.PAUSED:
      return "#ED8936";
    case TRANSACTION_STATUS.INDEFINITE_PAUSE:
      return "#D53F8C";
    default:
      return "#CBD5E0";
  }
}

export const stateNames: string[] = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

export const WEEKLY_PAYMENTS_REDUCTION_MULTIPLIER = 0.021;

export const MAX_FILE_UPLOAD_SIZE = 25; // in MB

export const CHART_COLORS = [
  "#f97316",
  "#a855f7",
  "#14b8a6",
  "#f43f5e",
  "#0ea5e9",
  "#ec4899",
  "#6366f1",
  "#22c55e",
  "#ef4444",
];

export const LENDERS = Array.from(
  new Set([
    "3 Point",
    "501 Advance LLC",
    "5G Funding LLC",
    "8fig Inc.",
    "Accord Business Funding  LLC",
    "Ace Funding Source LLC",
    "ACH Capital West LLC",
    "Advance American Funding",
    "Advance Service Group LLC",
    "Advance Servicing Inc.",
    "Advance Source Capital Group LLC",
    "Advanced Energy Services Inc.",
    "Advantage Funders",
    "Advantage Platform Services Inc. dba Advantage Capital Funding",
    "AFK Inc",
    "AI Funding USA  a New York company",
    "AJ Equity Group LLC",
    "Alliance Funding Group",
    "Alpine Advance 5 LLC",
    "Alternative Funding Group Corp.",
    "American Capital Group Inc.",
    "American Choice Capital LLC",
    "American Financial Center Inc.",
    "Amerifi Capital LLC",
    "Amsterdam Capital Solutions LLC",
    "Amur Equipment Inc.",
    "AP Advance Group LLC",
    "Apex Funding Source",
    "Arcarius LLC",
    "ARF Financial LLC",
    "Arsenal Funding",
    "Atipana Capital LLC dba Orange-Funding",
    "Atlantic Advance LLC",
    "ATX MCA Fund I LLC",
    "Auction Credit Enterprises LLC",
    "Austin Business Finance LLC",
    "Avion Funding",
    "B&G Capital LLC",
    "B2B Capital Providers",
    "Baker Solutions Inc.",
    "Bambu Finance",
    "BHB722 Inc. dba BHB Funding",
    "Bitty Advance",
    "Bizfi Funding",
    "Bizfund  LLC",
    "Black Olive Capital LLC",
    "Blade Funding Inc.",
    "Blue Bridge Capital LLC",
    "Blue Creek Funding LLC",
    "Blue Fund Group LLC",
    "Blue Sky Advance Inc.",
    "Bluebird Lending  LLC",
    "Bluevine",
    "BMT Capital Group Inc.",
    "Bow Apple Capital LLC",
    "Breakout Capital",
    "Breeze Advance LLC",
    "Brickstone Group LLC",
    "Broadway Advance",
    "Broadway Capital Funding LLC",
    "Business Capital Providers Inc.",
    "Byzfunder NY LLC",
    "Byzfunder NY LLC dba Tandem Advance",
    "C2 Advance LLC",
    "C6 Capital Funding",
    "Can Capital  Inc.",
    "Canacap",
    "Canfield Capital LLC",
    "Canon Advance LLC",
    "CapAssist",
    "Capdeck Capital",
    "Capifi LLC",
    "Capital Assist LLC",
    "Capital Dude LLC",
    "Capital Express Group LLC",
    "Capital Express Inc.",
    "Capital Funding Asap LLC",
    "Capital Minutes LLC",
    "Capital Resource International  Inc.",
    "Capitalize Group LLC",
    "CAPS Funding LLC",
    "Capytal. com",
    "Cardiff Funding LLC",
    "Cash Cloud Inc. dba Coin Cloud",
    "Cash Depot Ltd. Corp.",
    "Cashable LLC",
    "CashFund",
    "Caymus Funding",
    "Caymus Funding Inc.",
    "Cedar",
    "Cedar Advance LLC",
    "Centra Funding LLC",
    "CFG Merchant Solutions LLC",
    "CFS CAP LLC",
    "CGAP Holdings LLC",
    "Charge Up Capital LLC",
    "Cheetah Capital",
    "Chrome Capital",
    "Churchill Capital Partners LLC",
    "Churchill Group",
    "City Capital NY",
    "Clear Cap Advance LLC",
    "Clearfund Solutions LLC dba Clearfund Solutions",
    "Cloudfund LLC",
    "Cobalt Funding Solutions LLC",
    "Coconut Funding Corp.",
    "Colonial Funding Network",
    "Commercial Financial Services",
    "Complete Business Solutions Group Inc.",
    "Coolidge Capital",
    "Core Capital LLC",
    "Core Funding Source LLC",
    "Creative Capital Funding",
    "Credibility Capital Inc.",
    "Credibly",
    "Credibly of Arizona",
    "Creditors Adjustment Bureau, Inc",
    "Crestview Financial LLC",
    "Cucumber Capital",
    "Custom Capital",
    "Cyberswift",
    "Diamond Advances LLC",
    "Diamond Stone Funding Inc.",
    "Diesel Funding LLC",
    "Direct Merchants Funding LLC",
    "Diverse",
    "Diverse Capital LLC",
    "Diversitas Holdings LLC",
    "DLP Funding LLC",
    "DMKA LLC",
    "Dreamspring",
    "Dunkin Group",
    "Dynamic Renewable Solutions Inc.",
    "Dynasty Capital 26 LLC",
    "E Advance Services",
    "Eagle Capital Corporation",
    "Eagle Eye Advance LLC",
    "Eagle Phillips NY LLC dba Uplyft Capital",
    "Eastern Funding LLC",
    "EBF - Everest Business Funding",
    "eFinancial Tree",
    "EIN Cap Inc.",
    "Electronic Merchant Systems",
    "Elevate Funding LLC",
    "Elite Business Capital LLC",
    "Emerald Group Holdings LLC",
    "Eminent Funding LLC",
    "Emmy Capital Group LLC",
    "EOM Advance LLC",
    "Epic Advance LLC",
    "Equita Advance LLC",
    "Essential Funding Group Inc.",
    "Expansion Capital Group LLC",
    "EZFund LLC aka EZ Fund LLC",
    "F.B.F. Capital LLC",
    "Family Business Fund LLC",
    "FC Market Place LLC",
    "Fenix Capital Funding LCL",
    "FinCoast Capital LLC",
    "Finova Capital LLC",
    "Finpoint Funding LLC",
    "Fintap",
    "Fintegra LLC",
    "Finvest LLC",
    "Firstline Advance LLC",
    "Five G Funding LLC",
    "Flash Funding LLC",
    "Flex Funding",
    "Flexibility Capital",
    "Fora Financial Advance LLC",
    "Forever Funding",
    "Formula Funding",
    "Fortiline",
    "Forward Financing",
    "Forwardline Financial LLC",
    "Fox Capital Group Inc",
    "Fox Capital Group Inc.",
    "Fox Holdings  LLC",
    "Fresh Funding",
    "Fresh Funding Solutions Inc.",
    "Front Capital LLC",
    "FTC Funding",
    "Fundamental Capital",
    "Fundation Group",
    "Fundfi Merchant Funding LCL",
    "Fundi Capital",
    "Funding Depot LLC",
    "Funding Network LLC",
    "Fundkite",
    "Fundomate Technologies Inc.",
    "Fundova",
    "Fusion Funding",
    "G&G Funding",
    "Gem Funding LLC",
    "Gemini",
    "Gemini Funding Group LLC",
    "GFE NY",
    "GLd Funding",
    "Global Horizion Funding LLC",
    "Global Merchant Cash Inc.",
    "Global One Financial",
    "Globex Funding LLC",
    "Green Grass Capital",
    "Green Note Capital Partners SPV LLC",
    "Greenbox Capital",
    "Greentree Advance",
    "Greenwich Business Capital LLC",
    "Greenwich Funding Group LLC",
    "Grid Market LLC dba Grid Funding",
    "GRP",
    "Harper Advance LLC",
    "Headway Capital LLC",
    "Heavenly Funding LLC",
    "HFH Capital LLC",
    "HFH RG Funding",
    "Hi Bar Capital LLC  a New York company",
    "High Flow Funding LLC",
    "High Speed Capital LLC",
    "Highland Hill Capital LLC",
    "Home Run Capital LLC",
    "Honest Funding LLC",
    "Hybrid Advance LLC",
    "Ibex Funding Group LLC",
    "ILend Adance",
    "Image Capital Partners LLC",
    "Infusion Capital",
    "Instafund Advance LLC",
    "Instafunders",
    "Instafunders LLC",
    "Integrity Corporate Funding Inc.",
    "IOU Central Inc.",
    "Irm Ventures Capital Llc",
    "Itria Ventires",
    "JRG Funding LLC",
    "Kalamata Capital Group LLC",
    "Kapitus",
    "Kapitus Servicing",
    "Kash Advance LLC",
    "Key Star Capital Fund",
    "Kinetic Advantage LLC",
    "Kinetic Direct Funding LLC",
    "Kings Funding Group LLC",
    "KMT Funding LLC",
    "Knight Capital LLC",
    "Knightbridge Funding LLC",
    "Kodiak Funding",
    "Komodo Capital",
    "KYF Global Partners LLC",
    "L A Arena Funding  LLC",
    "Landmark Funding Group LLC",
    "Last Chance Funding Inc.",
    "Legend Advance Funding",
    "Lendbug LLC",
    "Lending Valley Inc.",
    "Lendini",
    "Lendora",
    "Lendora Capital",
    "Lendr",
    "Lendr",
    "LFG Funding Group",
    "Libertas Funding LLC",
    "Lifetime Funding",
    "Liftfund Inc.",
    "Limelight Advance LLC",
    "Lionheart Funding",
    "Liquidbee LLC",
    "LIQUIDiBEE",
    "Litefund Solutions LLC",
    "LIV Funding LLC",
    "Llc Odk Capital",
    "LoanMe LLC",
    "Madison Advance LLC",
    "Maison Capital Group Inc.",
    "Mantis Funding",
    "Marta",
    "Maverick Funding LLC",
    "Maybourne Funding LLC",
    "Mayfair Business Capital LLC",
    "MCA Fixed Payment LLC",
    "MCA Fixed Payment LLC",
    "MCA Servicing Co.",
    "MCA Ventures LLC",
    "MCA World Inc.",
    "McKenzie Capital LLC",
    "Meged Funding Group Corp.",
    "Merchant Advance LLC",
    "Merchant Capital Collections LLC",
    "Merchant Capital Source",
    "Merchant Cash and Capital LLC",
    "Mercury Funding Group Inc.",
    "Merk Funding Inc.",
    "Midwest Business Funding Inc.",
    "Millston Funding Inc.",
    "Mint Funding Inc.",
    "ML Factors Funding LLC",
    "MM Funding Group",
    "MNS Funding",
    "Moneywell GRP",
    "Moneywell GRP LLC",
    "Mr. Advance LLC",
    "Mulligan Funding LLC",
    "MW GRP Capital",
    "Mynt Advance",
    "Narai Ventures LLC",
    "National Funding",
    "NBFII Inc.",
    "Newco Capital Group",
    "Newtek Small Business Finance LLC",
    "Nextstone Capital LLC",
    "Nextwave Enterprises LLC",
    "Novac Equities LLC",
    "Novo Advance LLC",
    "Novus Capital Funding",
    "Oak Advance",
    "Oakwood Business Funding LLC",
    "Ocean Advance LLC",
    "ODK Capital  LLC",
    "Olive Capital Group LLC",
    "On Deck Capital",
    "Orange Advance LLC",
    "Orange Capital",
    "Overnight Capital",
    "Pac Western Financial",
    "Palmetto Capital Ventures",
    "PAR Funding",
    "Parkside Funding Group LLC",
    "Parkview Advance LLC",
    "Pathward NA",
    "Paychex Advance LLC",
    "Paz Funding Source",
    "PDM Capital LLC",
    "Pear",
    "Pearl Beta Funding LLC",
    "Pearl Capital Business Funding LLC dba Saturn Encore Funding",
    "Pearl Delta Funding",
    "Pinnacle Business Funding LLC",
    "Pirs Capital LLC",
    "Platinum Advances LLC",
    "Platinum Asset Funding LLC",
    "Platnium Rapid Funding Group",
    "Pointone Capital LLC",
    "Preferred Funding LLC",
    "Premier Capital Funding LLC",
    "President Funding Group LLC",
    "Presto-Max",
    "Primary Capital Funding LLC",
    "Prime Advance LLC",
    "Prince Advance Funding LLC dba Prince Funding LLC",
    "Principis Capital LLC",
    "Propella Capital LLC",
    "Prosperum Capital Partners LLC",
    "Proventure Capital Funding LLC",
    "PS Funding Inc.",
    "QFS Capital LLC",
    "QS Capital Partners LLC",
    "Quarterspot Inc",
    "Queen Funding",
    "Quick Bridge Funding",
    "QuickFix",
    "Quickstone Capital Solutions",
    "Quik Capital LLC",
    "Quikstone Capital Solutions",
    "Radium2 Capital",
    "Rainbow Advance LLC",
    "Rapid Capital Funding",
    "Rapid Financial Services LLC",
    "RDM Capital Funding",
    "Reassure Funding LLC",
    "Rebound Capital Group",
    "Red Target LLC",
    "Redstone Advance",
    "Reef Funding Group",
    "Regal Capital Inc.",
    "Reliable Fast Cash LLC",
    "Reliance Financial LLC",
    "Reliant Funding",
    "Reserve Capital Management",
    "Retail Capital LLC",
    "Revenued LLC",
    "RFS Business Funding",
    "Rival Funding",
    "River Capital Partners LLC",
    "Robin Funding Group LLC",
    "ROC Funding Group LLC",
    "Rocket Capital NY LLC",
    "Royal Business Group LLC",
    "Royal Oak Funding LLC",
    "RSS Recovery Solutions Services LLC",
    "Samson MCA LLC",
    "Saturn Encore Funding",
    "Saucheen Financing LLC",
    "Seamless Capital Group LLC",
    "Secure Business Servicing",
    "Secure Funding Source",
    "Select Funding LLC",
    "Settle Funding LLC",
    "Shine Capital Group",
    "Shor Capital",
    "Silverline Services",
    "Simpli Funding",
    "Simply Funding  LLC",
    "SK2 Capital LLC",
    "Skyinance Holdings LLC",
    "Slate Advance",
    "Small Business Financial Solutions",
    "Smart Business Funding",
    "SOS Capital",
    "Spark Funding LLC",
    "Spartan Business Solutions LLC",
    "Spartan Capital",
    "Specialty Capital LLC",
    "Speedy Funding LLC",
    "SPG Advance",
    "Spin Collections LLC",
    "Spine Injury Solutions Inc.",
    "Splash Advance LLC",
    "Spotlight Advisors LLC",
    "Spring Funding",
    "Square Advance",
    "Square Funding LLC dba Square Advance",
    "SRB Capital LLC",
    "Stack Capital LLC",
    "Star Advance LLC",
    "Strategic Funding Source",
    "Strategic Funding Source Inc.",
    "Streamlined Consultants Inc.",
    "Summit Funding Group Inc.",
    "Suncoast Funding Group",
    "Superfast Capital",
    "Surfside Capital",
    "Sutton Capital Funding LLC ",
    "Sutton Funding",
    "Swift Funding Source Inc.",
    "Syndicate Group USA Inc.",
    "Tbf Financial I Llc",
    "Terrapin Funding LLC",
    "The Avanza Group LLC",
    "The Business Backer",
    "The Fundworks LLC",
    "The Grace Company",
    "The LCF Group - Last Chance Funding",
    "The Merchant Marketplace Corp.",
    "The Merchants Company",
    "The Money Store Funding",
    "The Smarter Merchant",
    "Thor Capital",
    "Throttle",
    "Throttle Funding LLC",
    "Torro",
    "TOT Capital",
    "Total Merchant Resources LLC",
    "Transport Funding LLC",
    "True Business Funding LLC",
    "TVT Capital LLC",
    "UFS West LLC",
    "Ultra Funding LLC",
    "Union Funding Source",
    "Unique Funding Solutions LLC",
    "Uptown Fund LLC",
    "Upward Capital",
    "Urus Funding LLC",
    "Vader Servicing LLC",
    "Velocity Capital",
    "Velocity Venture Partners LLC",
    "VenConnect LLC",
    "Vertex Funding LLC",
    "Vista Point Services LLC",
    "Vital Capital",
    "Vitalcap Fund",
    "Vitalcap Fund",
    "Vivian Capital Group LLC",
    "Vox Funding LLC",
    "Walco Funding LLC",
    "Wall Street Funding",
    "Waterview Capital LLC",
    "Webfund LLC",
    "WeFund",
    "Wellen Capital",
    "Westwood Funding Solutions LLC",
    "WG Fund LLC",
    "Wide Capital Express Inc.",
    "World Business Lenders",
    "World Global Fund LLC",
    "Worldwide Capital Management",
    "WWCM",
    "Wynwood Capital Group LLC",
    "XL Funding LLC",
    "Yarrow Financial",
    "Yellowstone Capital LLC",
    "Yes Capital Group LLC",
    "Yes Funding Services LLC",
    "Yes Lender LLC",
    "YNS Funding LLC",
    "Zahav Asset Management",
    "Zinch",
  ]),
).map((el) => ({ label: el, value: el }));

export const HOTPROSPECTOR_LINK = "https://s1.hotprospector.com/glu/calendar/10154/Ralph1230009321";

export const MORTGAGE_CALCULATOR_CITY_LIST = [
  "Austin",
  "Denver",
  "Seattle",
  "Portland",
  "Scottsdale",
  "Salt Lake City",
  "Nashville",
  "Dallas",
  "Charlotte",
  "Tampa",
  "Atlanta",
  "Raleigh",
  "San Antonio",
  "Phoenix",
  "Orlando",
  "Miami",
  "San Diego",
  "Minneapolis",
  "Boulder",
  "Honolulu",
];

export enum ORGANISATIONS {
  FCDS25 = "fcds25",
  // ELITED = "edp001",
  SSA001 = "ssa001",
  BFS001 = "bfs001",
  EFP001 = "efp001",
  DEFAULT = "default",
}

export const DAY_OPTIONS = [
  // { label: "Sunday", value: 7 },
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  // { label: "Saturday", value: 6 },
];
